import React, { useState } from 'react';
import useAppStore from '../../../store';
import TeamMemberRow from '../components/TeamMemberRow';
import RoleSelector from '../components/RoleSelector';
import DataModeBadge from '../../../components/feedback/DataModeBadge';

/**
 * Team tab (full-live-cutover §3, Settings build). Same posture as the
 * profile: NO team write API exists this batch (PB users are superuser-only
 * — no safe user-list read exists either), so the roster stays the labeled
 * SAMPLE seed and every mutation is an explicitly "mock" local action. The
 * badge + notes state this; nothing presents as live.
 */
function TeamTab() {
  const session = useAppStore((state) => state.session);
  const teamMembers = useAppStore((state) => state.teamMembers);
  const inviteTeamMember = useAppStore((state) => state.inviteTeamMember);
  const setTeamMemberRole = useAppStore((state) => state.setTeamMemberRole);
  const removeTeamMember = useAppStore((state) => state.removeTeamMember);
  const recordAudit = useAppStore((state) => state.recordAudit);
  const canManage = session?.role === 'agency-admin';

  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('operator');
  const [inviteNote, setInviteNote] = useState('');

  const handleInvite = (event) => {
    event.preventDefault();
    const email = inviteEmail.trim();
    if (!email || !email.includes('@')) {
      setInviteNote('Enter a valid work email.');
      return;
    }
    if (teamMembers.some((member) => member.email.toLowerCase() === email.toLowerCase())) {
      setInviteNote('That person is already on the team.');
      return;
    }
    inviteTeamMember({ email, role: inviteRole });
    setInviteEmail('');
    setInviteNote(`Invite queued for ${email} (mock — no email is sent yet).`);
  };

  const handleResendInvite = (member) => {
    recordAudit('team.invite_resent', member.email);
    setInviteNote(`Invite re-sent to ${member.email} (mock).`);
  };

  return (
    <div className="settings-tab">
      <p className="settings-note" role="note">
        <DataModeBadge mode="sample" label="Sample roster — no team read/write API yet (PB users are superuser-only)" />{' '}
        Invites and role changes below are local demonstrations; no email is
        sent and no user record is touched.
      </p>
      {canManage ? (
        <form className="settings-invite" onSubmit={handleInvite}>
          <div className="settings-form-grid">
            <div className="settings-field">
              <label htmlFor="invite-email">Invite by email</label>
              <input
                id="invite-email"
                onChange={(event) => setInviteEmail(event.target.value)}
                placeholder="teammate@example.com"
                value={inviteEmail}
              />
            </div>
            <div className="settings-field">
              <label htmlFor="invite-role">Role</label>
              <RoleSelector id="invite-role" onChange={setInviteRole} value={inviteRole} />
            </div>
          </div>
          <div className="settings-form-foot">
            <button className="portal-primary-button" type="submit">Send invite</button>
            {inviteNote && <span className="settings-saved" role="status">{inviteNote}</span>}
          </div>
        </form>
      ) : (
        <p className="settings-note">Only agency-admins can manage the team.</p>
      )}

      <div className="portal-table-wrap">
        <table className="portal-table">
          <thead>
            <tr>
              <th scope="col">Member</th>
              <th scope="col">Role</th>
              <th scope="col">Status</th>
              <th scope="col">Last active</th>
              <th scope="col"><span className="sr-only">Actions</span></th>
            </tr>
          </thead>
          <tbody>
            {teamMembers.map((member) => (
              <TeamMemberRow
                canManage={canManage}
                key={member.id}
                member={member}
                onRemove={removeTeamMember}
                onResendInvite={handleResendInvite}
                onRoleChange={setTeamMemberRole}
                selfId={session?.user?.id}
              />
            ))}
          </tbody>
        </table>
      </div>
      {!teamMembers.length && <p className="settings-note">No team members yet — invite your first operator above.</p>}
    </div>
  );
}

export default TeamTab;
